import { useState } from 'react';

import { TEST_BATCH_TYPE, VALIDATION_BATCH_TYPE } from '../../constants';
import type { AccuracyBatchOption } from '../../types/response';

interface UseBatchTypeSectionArgs {
  batches: AccuracyBatchOption[];
  onSelectBatch: (batch: AccuracyBatchOption) => void;
  onConfirmTestBatch: (batch: AccuracyBatchOption) => void;
  onSwitchToValidation: (batch: AccuracyBatchOption) => void;
}

export interface UseBatchTypeSectionResult {
  pendingTestBatch: AccuracyBatchOption | null;
  validationBatch: AccuracyBatchOption | undefined;
  handleCardSelect: (batch: AccuracyBatchOption) => void;
  handleDialogCancel: () => void;
  handleDialogConfirm: () => void;
  handleSwitchToValidation: () => void;
}

/**
 * Owns the ephemeral "which batch is awaiting test-set confirmation" UI state.
 * The atomic reducer transitions themselves (selectBatch / confirmTestBatchSelection /
 * switchToValidationBatch) live one level up, in `useGenerateAccuracyScreen`.
 */
export const useBatchTypeSection = ({
  batches,
  onSelectBatch,
  onConfirmTestBatch,
  onSwitchToValidation,
}: UseBatchTypeSectionArgs): UseBatchTypeSectionResult => {
  const [pendingTestBatch, setPendingTestBatch] = useState<AccuracyBatchOption | null>(null);
  const validationBatch = batches.find((b) => b.batchType === VALIDATION_BATCH_TYPE);

  const handleCardSelect = (batch: AccuracyBatchOption) => {
    if (batch.batchType === TEST_BATCH_TYPE) {
      setPendingTestBatch(batch);
      return;
    }
    onSelectBatch(batch);
  };

  const handleDialogCancel = () => setPendingTestBatch(null);

  const handleDialogConfirm = () => {
    if (pendingTestBatch) {
      onConfirmTestBatch(pendingTestBatch);
    }
    setPendingTestBatch(null);
  };

  const handleSwitchToValidation = () => {
    if (validationBatch) {
      onSwitchToValidation(validationBatch);
    }
  };

  return {
    pendingTestBatch,
    validationBatch,
    handleCardSelect,
    handleDialogCancel,
    handleDialogConfirm,
    handleSwitchToValidation,
  };
};
